import { useEffect, useState } from 'react';

import { getMyProfile } from '../../../core/identity-access/api/profile';

export interface TeacherVerificationView {
  loading: boolean;
  /**
   * `true` / `false` once known, `undefined` when the profile could not be
   * read.
   *
   * Undefined is a real third state and callers must not collapse it to
   * `false`: refusing to offer publishing because a profile call timed out
   * would block a verified teacher over a network blip, and the server enforces
   * the rule anyway.
   */
  verified?: boolean;
  status?: string;
}

/**
 * S11.1 — whether the platform currently treats this teacher as verified.
 *
 * `teacherProfile.verificationStatus` is what the publish gate checks. Screens
 * previously inferred publish-ability from the *application* state, which
 * describes where a teacher sits in review: an approved application whose
 * activation never completed produced a Publish button that always 403'd, with
 * nothing on screen to explain it.
 */
export function useTeacherVerification(enabled = true): TeacherVerificationView {
  const [loading, setLoading] = useState(enabled);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [readable, setReadable] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const profile = await getMyProfile();
        if (cancelled) return;
        setStatus(profile.teacherProfile?.verificationStatus);
        setReadable(true);
      } catch {
        // Supporting context on a screen that has its own error handling: a
        // failure here leaves verification unknown rather than blanking the
        // page or asserting a teacher is unverified.
        if (!cancelled) setReadable(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return {
    loading,
    verified: readable ? status === 'VERIFIED' : undefined,
    status,
  };
}
